// Builds the human-readable "Scheduling Notes" shown for a rotation/preceptor.
// Mirrors the logic used on the rotation detail page so the search result cards
// display the same notes. Source data:
//   - seasons_unavailable: string[] like "Spring 2026" or "Spring 2026 | Arcadia University"
//   - restricted_universities: string[] of university names the preceptor won't accept

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';

const addUniqueNote = (notes: string[], note: string) => {
  if (!notes.includes(note)) notes.push(note);
};

const formatUnavailablePeriodNote = (period: string) =>
  `This preceptor is not accepting students for ${period}.`;
const formatUniversityUnavailablePeriodNote = (university: string, period: string) =>
  `This preceptor is not accepting ${university} students for ${period}.`;
const formatWholeYearNote = (year: string) =>
  `This preceptor is not accepting students for the whole year of ${year}.`;
const formatUniversityWholeYearNote = (university: string, year: string) =>
  `This preceptor is not accepting ${university} students for the whole year of ${year}.`;
const formatRestrictedUniversityNote = (university: string) =>
  `This preceptor is not accepting ${university} students.`;

export const toSeasonList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  if (typeof value === 'string' && value.trim().length > 0) return [value.trim()];
  return [];
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  return [];
};

export interface SchedulingNotesResult {
  /** Sentences to display, e.g. "This preceptor is not accepting students for Spring 2026." */
  notes: string[];
  /** Normalized unavailable period labels, e.g. ["Spring 2026", "Whole Year 2026"]. */
  normalizedSeasonsUnavailable: string[];
}

export function buildSchedulingNotes(
  seasonsUnavailable: unknown,
  restrictedUniversities?: unknown
): SchedulingNotesResult {
  const seasonsUnavailableRaw = toSeasonList(seasonsUnavailable);
  const restricted = toStringArray(restrictedUniversities);

  const parsedUnavailable = seasonsUnavailableRaw
    .map(entry => {
      const trimmed = entry.trim();
      if (!trimmed) return null;
      const [periodPartRaw, audienceRaw] = trimmed.split('|').map(part => part.trim());
      const periodPart = periodPartRaw || trimmed;
      const match = periodPart.match(/^([A-Za-z]+)\s+(\d{4})$/);
      const token = match ? match[1].toLowerCase() : '';
      const year = match ? match[2] : '';
      const isWholeYear = token === 'year';
      const displayLabel = isWholeYear
        ? `Whole Year ${year}`
        : match
          ? `${capitalize(match[1])} ${match[2]}`
          : periodPart;
      const noteLabel = match ? `${capitalize(match[1])} ${match[2]}` : periodPart;
      const audience = audienceRaw || null;
      return { period: displayLabel, noteLabel, audience, isWholeYear, year };
    })
    .filter(
      (item): item is { period: string; noteLabel: string; audience: string | null; isWholeYear: boolean; year: string } =>
        Boolean(item)
    );

  const normalizedSeasonsUnavailable = parsedUnavailable.map(item => item.period);

  const notes: string[] = [];
  parsedUnavailable.forEach(item => {
    const hasUniversityAudience = item.audience && item.audience.toLowerCase() !== 'all';
    if (item.isWholeYear) {
      if (hasUniversityAudience) {
        addUniqueNote(notes, formatUniversityWholeYearNote(item.audience as string, item.year));
      } else {
        addUniqueNote(notes, formatWholeYearNote(item.year));
      }
    } else if (hasUniversityAudience) {
      addUniqueNote(notes, formatUniversityUnavailablePeriodNote(item.audience as string, item.noteLabel));
    } else {
      addUniqueNote(notes, formatUnavailablePeriodNote(item.noteLabel));
    }
  });

  if (restricted.length) {
    restricted.forEach(u => addUniqueNote(notes, formatRestrictedUniversityNote(u)));
  }

  return { notes, normalizedSeasonsUnavailable };
}
